import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { CSGComponent } from './CSGComponent.js';

import type { CSGComponentOptions } from './CSGComponent.js';
import type { ActiveManifold, ManifoldWrapper } from '../../csg/ManifoldWrapper.js';


export interface CSGCylinderComponentOptions extends CSGComponentOptions {
  /** The height of the cylinder */
  height?: number;
  /** The radius at the bottom of the cylinder */
  radiusBottom?: number;
  /** The radius at the top of the cylinder */
  radiusTop?: number;
  /** The number of segments around the cylinder */
  segments?: number;
  /** Whether the cylinder should be centered */
  centered?: boolean;
}

/** Cylinder shape for CSG operations. */
@EngineClass('CSGCylinderComponent')
export class CSGCylinderComponent extends CSGComponent<CSGCylinderComponentOptions> {

  @property({ category: 'CSG', type: 'number', min: 0.01, step: 0.1, decimals: 2, description: 'Height of the cylinder' })
  protected height: number = 1;

  @property({ category: 'CSG', type: 'number', min: 0.01, step: 0.1, decimals: 2, description: 'Radius at the bottom' })
  protected radiusBottom: number = 0.5;

  @property({ category: 'CSG', type: 'number', min: 0.01, step: 0.1, decimals: 2, description: 'Radius at the top' })
  protected radiusTop: number = 0.5;

  @property({ category: 'CSG', type: 'number', min: 3, max: 128, step: 1, decimals: 0, description: 'Number of segments' })
  protected segments: number = 32;

  @property({ category: 'CSG', description: 'Whether the cylinder is centered at origin' })
  protected centered: boolean = true;

  public override initialize(options?: CSGCylinderComponentOptions): void {
    super.initialize(options);
    this.height = options?.height ?? this.height;
    this.radiusBottom = options?.radiusBottom ?? this.radiusBottom;
    this.radiusTop = options?.radiusTop ?? this.radiusTop;
    if (options?.radiusTop === undefined && options?.radiusBottom !== undefined) this.radiusTop = options.radiusBottom;
    this.segments = options?.segments ?? this.segments;
    this.centered = options?.centered ?? this.centered;
  }

  public override postLoad(): void {
    super.postLoad();
  }

  public override createManifold(manifoldWrapper: ManifoldWrapper, isRoot: boolean = false): ActiveManifold | null {
    if (!this.enabled || this._pendingRemoval) return null;

    const s = this.scale;
    const scaleXZ = (s.x + s.z) / 2;
    const manifold = manifoldWrapper.cylinder(
      this.height * s.y,
      this.radiusBottom * scaleXZ,
      this.radiusTop * scaleXZ,
      this.segments,
      this.centered
    );
    manifold.setMaterial(this.material);

    if (!isRoot) {
      const deg = 180 / Math.PI;
      manifold.rotate(new THREE.Vector3(
        this.rotation.x * deg,
        this.rotation.y * deg,
        this.rotation.z * deg,
      ));
      manifold.translate(this.position);
    }

    return manifold;
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Cylinder';
  }
}
